import { useState, useEffect, useRef } from 'react'
import s from './LoginForm.scss'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Container, Col, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { Turnstile } from '@marsidev/react-turnstile'
import { Spinner } from 'components/ideal-core-ui'
import { URLS } from 'utils/constants'
import { Link } from 'react-router-dom'
import { isMobileOnly } from 'react-device-detect'
import { makeGetRequest } from 'utils/api'

const tenant = window.location.hostname.split('.')[0]
const TURNSTILE_SITE_KEY =
  typeof window !== 'undefined' && window.Cypress ? '' : process.env.TURNSTILE_SITE_KEY

const LoginForm = props => {
  const [logo, setLogo] = useState('')
  const [backgroundColor, setBackgroundColor] = useState('#51525e')
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileRef = useRef(null)
  const turnstileRequired = Boolean(TURNSTILE_SITE_KEY)

  useEffect(() => {
    if (tenant === 'localhost') return
    ;(async () => {
      const { logo, backgroundColor } = await makeGetRequest('/v1/organization/get-logo', {
        headers: {
          'x-tenant-schema': tenant
        }
      })
      setLogo(logo)
      setBackgroundColor(backgroundColor)
    })()
  }, [])

  return (
    <Formik
      props={props}
      initialValues={{ email: '', password: '' }}
      onSubmit={(values, { setSubmitting }) => {
        props
          .formSubmit(
            { ...values, turnstileToken },
            {
              onError: () => {
                setTurnstileToken('')
                turnstileRef.current?.reset()
              }
            }
          )
          .then(() => setTimeout(() => setSubmitting(false), 2000))
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string().email('Invalid email address').required('Email is required!'),
        password: Yup.string().required('Password field is required!')
      })}
    >
      {props => {
        const { values, touched, errors, isSubmitting, handleBlur, handleChange, handleSubmit } =
          props

        return (
          <Container className={s.container}>
            <div
              className={s.header}
              style={
                logo
                  ? {
                      backgroundImage: `url(${logo})`,
                      backgroundColor,
                      backgroundSize: '150px 75px'
                    }
                  : {}
              }
            />
            <Form className={s.form} onSubmit={handleSubmit}>
              <p className={s.title}>Welcome to iDeal CRM</p>
              {isMobileOnly && (
                <Alert color="info">
                  The iDeal Sales CRM mobile app is coming soon. You can continue your iDeal
                  experience but for better usability tablet size is minimum recommended for the web
                  app.
                </Alert>
              )}
              <Col>
                <FormGroup className={s.group}>
                  <Label for="email">Your Email Address</Label>
                  <Input
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    name="email"
                    placeholder="email@example.com"
                    disabled={isSubmitting}
                  />
                  {errors.email && touched.email && (
                    <div className={s.inputFeedback}>{errors.email}</div>
                  )}
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="password">Your password</Label>
                  <Input
                    type="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    name="password"
                    placeholder="Password"
                    disabled={isSubmitting}
                  />
                  {errors.password && touched.password && (
                    <div className={s.inputFeedback}>{errors.password}</div>
                  )}
                  <Link to={URLS.RESET_PASSWORD} className={s.noAccount}>
                    Reset your password
                  </Link>
                </FormGroup>
              </Col>
              {turnstileRequired && (
                <Col>
                  <FormGroup className={s.group} data-cy="turnstile_widget">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={setTurnstileToken}
                      onExpire={() => setTurnstileToken('')}
                      onError={() => setTurnstileToken('')}
                    />
                  </FormGroup>
                </Col>
              )}
              <Button
                className={s.loginBtn}
                type="submit"
                disabled={isSubmitting || (turnstileRequired && !turnstileToken)}
              >
                {isSubmitting ? <Spinner /> : 'Login'}
              </Button>
              <div className={s.clearfix} />
            </Form>
          </Container>
        )
      }}
    </Formik>
  )
}

export default LoginForm
